import type {Comment, SuggestedChangeLineRange, Thread} from '@github-ui/diff-lines/conversation-types'
import {CommentReviewVariantType, SuggestionStates} from '@github-ui/diff-lines/conversation-types'
import {getLineRangesFromDiffEntries} from '@github-ui/diff-lines/helpers/suggested-changes'
import {extractSuggestionFromMarkdown} from '@github-ui/diff-lines/extract-suggestion-content'

import {buildCommentSnapshot} from './comment-snapshot'
import type {UnifiedBatchItem, UnifiedBatchItemThreadHint} from '../stores/slices/unified-batch'

type ThreadComment = Pick<Comment, 'automatedComment' | 'body' | 'databaseId' | 'id' | 'reviewVariantType' | 'state'>

type BuildThreadItemOptions = {
  filePath?: string
  thread: Thread
}

function getThreadId(thread: Thread | undefined): number | null {
  if (!thread) return null

  const threadId = Number(thread.id)
  return Number.isFinite(threadId) ? threadId : null
}

function getThreadPath(thread: Thread, filePath?: string): string | undefined {
  return filePath ?? thread.positioning?.path ?? thread.path
}

function getThreadIsOutdated(thread: Thread | undefined): boolean {
  if (!thread) return false

  return Boolean(thread.positioning?.isOutdated ?? thread.isOutdated ?? thread.subject?.isOutdated)
}

export function getFirstSubmittedComment(thread: Thread): Comment | undefined {
  return thread.commentsData?.comments.find(comment => comment.state?.toUpperCase() === 'SUBMITTED')
}

function isAutomatedReviewComment(comment: ThreadComment): boolean {
  return (
    comment.reviewVariantType === CommentReviewVariantType.Copilot ||
    comment.reviewVariantType === CommentReviewVariantType.Automated
  )
}

function getAutomatedSuggestionLineRanges(comment: ThreadComment): Array<{
  path: string
  lineRange: SuggestedChangeLineRange
}> {
  const diffEntries = comment.automatedComment?.suggestion?.diffEntries ?? []
  return getLineRangesFromDiffEntries(diffEntries)
}

function hasAutomatedSuggestion(comment: ThreadComment): boolean {
  const automatedComment = comment.automatedComment

  // Only present CCR suggestions are actionable; applied or outdated suggestions
  // should be batched as the comment itself instead.
  return Boolean(automatedComment?.suggestion && automatedComment.suggestionState === SuggestionStates.Present)
}

/**
 * Builds a unified batch item from a review thread's first submitted comment.
 *
 * Returns store-addable whole-comment items, or a thread-level hint when a
 * human comment contains fenced suggestions and the row batch toggle should be
 * hidden until a specific suggestion block is selected.
 */
export function buildUnifiedBatchItemFromThread({
  filePath,
  thread,
}: BuildThreadItemOptions): UnifiedBatchItem | UnifiedBatchItemThreadHint | undefined {
  const comment = getFirstSubmittedComment(thread)
  if (!comment || comment.databaseId == null) return undefined

  const path = getThreadPath(thread, filePath)
  if (!path) return undefined

  const threadId = getThreadId(thread)
  const isOutdated = getThreadIsOutdated(thread)
  const base = {
    commentGlobalId: comment.id,
    commentDatabaseId: comment.databaseId,
    isOutdated,
    path,
    threadId,
    ...buildCommentSnapshot(comment),
  }

  if (isAutomatedReviewComment(comment)) {
    if (hasAutomatedSuggestion(comment)) {
      const pathLineRanges = getAutomatedSuggestionLineRanges(comment)

      return {
        ...base,
        kind: 'ccr-with-suggestion',
        hasSuggestion: true,
        suggestionIndex: undefined,
        lineRange: pathLineRanges[0]?.lineRange,
        pathLineRanges: pathLineRanges.length > 0 ? pathLineRanges : undefined,
        suggestionFiles: comment.automatedComment?.suggestion?.diffEntries ?? [],
      }
    }

    return {
      ...base,
      kind: 'ccr-without-suggestion',
      hasSuggestion: false,
      suggestionIndex: undefined,
      lineRange: undefined,
    }
  }

  if (extractSuggestionFromMarkdown(comment.body ?? '') !== null) {
    return {
      ...base,
      kind: 'human-with-suggestion',
      hasSuggestion: true,
      suggestionIndex: undefined,
      lineRange: undefined,
    }
  }

  return {
    ...base,
    kind: 'human-without-suggestion',
    hasSuggestion: false,
    suggestionIndex: undefined,
    lineRange: undefined,
  }
}
